import { useEffect, useState, useMemo } from "react";
import {
  FileText, MagnifyingGlass, CheckCircle, XCircle, Eye, ArrowsClockwise,
  IdentificationBadge, CreditCard, MapPinLine, ClockCounterClockwise,
  Hash, ShieldCheck, Package,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useDepots, fmtDepot } from "@/lib/depots";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const STATES = ["Submitted", "Under Review", "Approved", "Ready for Collection", "Collected", "Rejected"];

export default function Applications() {
  const depotMap = useDepots();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kindTab, setKindTab] = useState("all");
  const [statusTab, setStatusTab] = useState("");
  const [query, setQuery] = useState("");
  const [reviewing, setReviewing] = useState(null); // { kind, id, ref, title }

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (kindTab !== "all") params.set("kind", kindTab);
      if (statusTab) params.set("status", statusTab);
      const { data } = await api.get(`/applications?${params.toString()}`);
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kindTab, statusTab]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      (it.ref || "").toLowerCase().includes(q) ||
      (it.holder_name || "").toLowerCase().includes(q) ||
      (it.mobile || "").includes(q) ||
      (it.depot || "").toLowerCase().includes(q),
    );
  }, [items, query]);

  // Pre-computed counts per status for tabs
  const counts = useMemo(() => {
    const c = { all: items.length };
    for (const s of STATES) c[s] = items.filter((i) => i.status === s).length;
    return c;
  }, [items]);

  return (
    <div className="p-4 lg:p-6 space-y-4 lg:space-y-5" data-testid="admin-applications">
      {/* Header */}
      <div>
        <h1 className="text-xl lg:text-2xl font-bold tracking-tight">Citizen Applications</h1>
        <p className="text-[12px] lg:text-sm text-muted-foreground mt-1 hidden sm:block">
          Pass &amp; Smart Transit Card requests submitted through MajheApp — review documents, approve and notify the depot.
        </p>
      </div>

      {/* Quick stats row — 2 rows × 3 cols on mobile so all 6 pills are visible, 5 cols on tablet+ */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-3" data-testid="apps-stats-strip">
        {STATES.map((s) => (
          <StatPill key={s} status={s} count={counts[s] || 0}
            active={statusTab === s}
            onClick={() => setStatusTab((cur) => cur === s ? "" : s)}/>
        ))}
      </div>

      {/* Kind tabs + search */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
        <Tabs value={kindTab} onValueChange={setKindTab}>
          <TabsList className="w-full md:w-auto">
            <TabsTrigger value="all" data-testid="apps-tab-all" className="flex-1 md:flex-none">All ({counts.all})</TabsTrigger>
            <TabsTrigger value="pass" data-testid="apps-tab-pass" className="flex-1 md:flex-none">Passes</TabsTrigger>
            <TabsTrigger value="card" data-testid="apps-tab-card" className="flex-1 md:flex-none">Cards</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex gap-2 items-center">
          <div className="relative flex-1 md:flex-none">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
            <Input data-testid="apps-search" placeholder="Search ref, holder, mobile…"
              value={query} onChange={(e) => setQuery(e.target.value)}
              className="pl-9 md:w-72"/>
          </div>
          <Button variant="outline" size="icon" className="md:hidden" onClick={load} data-testid="apps-reload-mobile" aria-label="Reload">
            <ArrowsClockwise size={16}/>
          </Button>
          <Button variant="outline" className="hidden md:inline-flex" onClick={load} data-testid="apps-reload">
            <ArrowsClockwise size={14} className="mr-1"/> Refresh
          </Button>
        </div>
      </div>

      {/* List — table on md+, cards on <md so depot staff can use a phone */}
      <div className="rounded-lg border bg-card overflow-hidden">
        {/* Desktop / tablet table */}
        <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-4 py-3 font-semibold">Kind</th>
              <th className="px-4 py-3 font-semibold">Reference</th>
              <th className="px-4 py-3 font-semibold">Holder</th>
              <th className="px-4 py-3 font-semibold">Title</th>
              <th className="px-4 py-3 font-semibold">Depot</th>
              <th className="px-4 py-3 font-semibold">Amount</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">
                <FileText size={28} className="mx-auto mb-2 opacity-60"/>
                No applications match the filters.
              </td></tr>
            )}
            {!loading && filtered.map((it) => (
              <tr key={`${it.kind}-${it.id}`} data-testid={`apps-row-${it.ref}`}
                className="border-t hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3">
                  <div className="inline-flex items-center gap-2 font-semibold">
                    {it.kind === "pass"
                      ? <IdentificationBadge size={16} className="text-violet-600"/>
                      : <CreditCard size={16} className="text-violet-600"/>}
                    {it.kind === "pass" ? "Pass" : "Card"}
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{it.ref}</td>
                <td className="px-4 py-3">
                  <div className="font-semibold">{it.holder_name || "—"}</div>
                  <a href={`tel:+91${it.mobile}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-[11px] text-muted-foreground hover:text-violet-700 hover:underline"
                    data-testid={`apps-call-${it.ref}`}>
                    +91 {it.mobile}
                  </a>
                </td>
                <td className="px-4 py-3">
                  <div className="text-[13px]">{it.title}</div>
                  {it.institution_name && (
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {it.institution_name}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1 text-[12px]">
                    <MapPinLine size={12} className="text-violet-600"/>{fmtDepot(it.depot, depotMap)}
                  </span>
                </td>
                <td className="px-4 py-3 font-semibold">₹{Number(it.amount || 0).toFixed(0)}</td>
                <td className="px-4 py-3"><StatusBadge status={it.status}/></td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="outline" data-testid={`apps-review-${it.ref}`}
                    onClick={() => setReviewing(it)}>
                    <Eye size={14} className="mr-1"/> Review
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        {/* Mobile card list — large tappable rows for depot counter staff */}
        <div className="md:hidden divide-y" data-testid="apps-mobile-list">
          {loading && <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="p-10 text-center text-sm text-muted-foreground">
              <FileText size={28} className="mx-auto mb-2 opacity-60"/>
              No applications match the filters.
            </div>
          )}
          {!loading && filtered.map((it) => (
            <button key={`${it.kind}-${it.id}`}
              data-testid={`apps-mobile-card-${it.ref}`}
              onClick={() => setReviewing(it)}
              className="w-full text-left p-4 active:bg-muted/40 transition-colors">
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2 font-semibold text-sm">
                  {it.kind === "pass"
                    ? <IdentificationBadge size={16} className="text-violet-600"/>
                    : <CreditCard size={16} className="text-violet-600"/>}
                  <span className="font-mono text-[11px] text-muted-foreground">{it.ref}</span>
                </div>
                <StatusBadge status={it.status}/>
              </div>
              <div className="font-bold text-[15px] leading-tight">{it.holder_name || "—"}</div>
              <a href={`tel:+91${it.mobile}`}
                onClick={(e) => e.stopPropagation()}
                data-testid={`apps-call-mobile-${it.ref}`}
                className="text-[12px] text-violet-700 font-semibold underline-offset-2 hover:underline">
                +91 {it.mobile}
              </a>
              <div className="text-[12px] text-muted-foreground">{it.title}</div>
              <div className="mt-2 flex items-center justify-between text-[12px]">
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <MapPinLine size={12} className="text-violet-600"/>{fmtDepot(it.depot, depotMap)}
                </span>
                <span className="font-bold">₹{Number(it.amount || 0).toFixed(0)}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {reviewing && (
        <ReviewDialog item={reviewing}
          onClose={() => setReviewing(null)}
          onChanged={() => { load(); }}/>
      )}
    </div>
  );
}

function StatPill({ status, count, active, onClick }) {
  const colors = {
    "Submitted":            "from-violet-100 to-violet-50 text-violet-700 border-violet-200",
    "Under Review":         "from-amber-100 to-amber-50 text-amber-700 border-amber-200",
    "Approved":             "from-purple-100 to-purple-50 text-purple-700 border-purple-200",
    "Ready for Collection": "from-indigo-200 to-indigo-100 text-indigo-800 border-indigo-300",
    "Collected":            "from-emerald-100 to-emerald-50 text-emerald-700 border-emerald-200",
    "Rejected":             "from-red-100 to-red-50 text-red-700 border-red-200",
  };
  const cls = colors[status] || colors["Submitted"];
  // Short label for mobile so it never truncates inside a 3-col grid
  const shortLabel = {
    "Submitted":            "Submitted",
    "Under Review":         "Review",
    "Approved":             "Approved",
    "Ready for Collection": "Ready",
    "Collected":            "Collected",
    "Rejected":             "Rejected",
  }[status] || status;
  return (
    <button onClick={onClick} data-testid={`apps-stat-${status.replace(/\s+/g, "-").toLowerCase()}`}
      className={`text-left rounded-lg border bg-gradient-to-br ${cls} p-2 md:p-3 transition-all
        ${active ? "ring-2 ring-violet-500 ring-offset-2" : "hover:shadow-sm"}`}>
      <div className="text-[9px] md:text-[11px] uppercase tracking-wider font-bold opacity-80 leading-tight">
        <span className="md:hidden">{shortLabel}</span>
        <span className="hidden md:inline">{status}</span>
      </div>
      <div className="text-lg md:text-2xl font-extrabold mt-0.5">{count}</div>
    </button>
  );
}

function StatusBadge({ status }) {
  const map = {
    "Submitted":            { bg: "bg-violet-100", fg: "text-violet-700" },
    "Under Review":         { bg: "bg-amber-100", fg: "text-amber-700" },
    "Approved":             { bg: "bg-purple-100", fg: "text-purple-700" },
    "Ready for Collection": { bg: "bg-indigo-700", fg: "text-white" },
    "Collected":            { bg: "bg-emerald-600", fg: "text-white" },
    "Rejected":             { bg: "bg-red-100", fg: "text-red-700" },
  };
  const s = map[status] || map["Submitted"];
  return (
    <span className={`inline-flex items-center text-[10px] font-extrabold uppercase tracking-wide rounded-full px-2.5 py-1
      ${s.bg} ${s.fg}`}>{status}</span>
  );
}

function ReviewDialog({ item, onClose, onChanged }) {
  const depotMap = useDepots();
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/applications/${item.kind}/${item.id}`);
      setFull(data);
    } catch { /* keep stale data */ }
  };

  useEffect(() => {
    let alive = true;
    api.get(`/applications/${item.kind}/${item.id}`).then(({ data }) => {
      if (alive) { setFull(data); setLoading(false); }
    }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [item]);

  const move = async (to_status) => {
    if (to_status === "Rejected" && !notes.trim()) {
      toast.error("Please add a rejection reason in Notes"); return;
    }
    setBusy(true);
    try {
      await api.post(`/applications/${item.kind}/${item.id}/review`, { to_status, notes });
      toast.success(`Marked as ${to_status}`);
      await refresh();            // keep dialog open; update full state in-place
      onChanged();                // refresh parent list silently
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const docs = full?.documents || {};
  const docKeys = Object.keys(docs);
  const isReady = full?.review_status === "Ready for Collection";
  const isCollected = full?.review_status === "Collected";

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        data-testid="apps-review-dialog"
        className="max-w-3xl gap-3
          max-md:left-0 max-md:top-0 max-md:translate-x-0 max-md:translate-y-0
          max-md:w-screen max-md:max-w-none max-md:h-[100dvh] max-md:max-h-none
          max-md:rounded-none max-md:p-0 max-md:flex max-md:flex-col
          md:max-h-[90vh] md:overflow-y-auto">
        <DialogHeader className="max-md:px-4 max-md:py-3 max-md:border-b max-md:shrink-0 max-md:bg-white max-md:sticky max-md:top-0 max-md:z-10">
          <DialogTitle className="flex items-center gap-2 text-base md:text-lg">
            {item.kind === "pass" ? <IdentificationBadge size={20}/> : <CreditCard size={20}/>}
            <span className="truncate">{item.title}</span>
          </DialogTitle>
          <div className="text-[12px] md:text-sm text-muted-foreground mt-1 leading-snug">
            <div className="md:inline">Ref: <span className="font-mono font-bold text-violet-700">{item.ref}</span></div>
            <div className="md:inline md:before:content-['_·_']">Holder: <b>{item.holder_name}</b></div>
            <div className="md:inline md:before:content-['_·_']">
              <a href={`tel:+91${item.mobile}`} data-testid="apps-dialog-call"
                className="text-violet-700 font-semibold underline-offset-2 hover:underline">
                +91 {item.mobile}
              </a>
            </div>
            <div className="md:inline md:before:content-['_·_']">Collect at <b>{fmtDepot(item.depot, depotMap)}</b></div>
          </div>
        </DialogHeader>

        <div className="max-md:flex-1 max-md:overflow-y-auto max-md:px-4 max-md:py-3 max-md:pb-32">
        {loading && <div className="text-center py-8 text-muted-foreground">Loading details…</div>}

        {!loading && full && (
          <div className="space-y-5">
            {/* Personal & address */}
            <section>
              <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Applicant Info</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <Info label="Category" value={full.category}/>
                {full.pass_type && <Info label="Pass Type" value={full.pass_type}/>}
                {full.balance !== undefined && <Info label="Initial Balance" value={`₹${full.balance}`}/>}
                {full.institution_name && <Info label="Institution" value={full.institution_name}/>}
                <Info label="Address" value={full.address}/>
                <Info label="City" value={full.city}/>
                <Info label="PIN" value={full.pincode}/>
                <Info label="Submitted" value={full.issued_at && new Date(full.issued_at).toLocaleString()}/>
                <Info label="Total Paid" value={`₹${full.total_paid || full.amount || 0}`}/>
              </div>
            </section>

            {/* Documents */}
            <section>
              <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
                Documents ({docKeys.length})
              </div>
              {docKeys.length === 0 && <div className="text-sm text-muted-foreground">No documents on file.</div>}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {docKeys.map((k) => (
                  <DocumentTile key={k} k={k} value={docs[k]}/>
                ))}
              </div>
            </section>

            {/* History */}
            <section>
              <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Activity Log</div>
              <div className="space-y-2">
                {(full.review_history || []).map((h, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <ClockCounterClockwise size={14} className="text-violet-600 mt-0.5"/>
                    <div className="flex-1">
                      <div className="font-semibold">{h.to} <span className="text-muted-foreground font-normal">· {h.by}</span></div>
                      {h.notes && <div className="text-muted-foreground text-[12px]">{h.notes}</div>}
                      <div className="text-[11px] text-muted-foreground/70">{new Date(h.at).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
                {(full.review_history || []).length === 0 && <div className="text-sm text-muted-foreground">No activity yet</div>}
              </div>
            </section>

            {/* Notes */}
            <section>
              <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground block mb-1.5">
                Reviewer Notes (sent to applicant)
              </label>
              <Input placeholder="e.g. Aadhar verified · physical pass printed"
                data-testid="apps-review-notes"
                value={notes} onChange={(e) => setNotes(e.target.value)}/>
            </section>

            {/* Pickup Confirmation — only when Ready for Collection or already Collected */}
            {(isReady || isCollected) && (
              <PickupConfirmSection item={item} full={full}
                onConfirmed={async () => { await refresh(); onChanged(); }}/>
            )}

            {/* Action row — sticky bottom bar on mobile, inline on desktop */}
            <section
              className="grid grid-cols-2 md:grid-cols-5 gap-2 pt-2 md:border-t
                max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0
                max-md:bg-white max-md:px-4 max-md:py-3 max-md:border-t max-md:shadow-[0_-4px_12px_rgba(0,0,0,0.06)]
                max-md:z-20 max-md:pb-[calc(env(safe-area-inset-bottom)+0.75rem)]">
              {STATES.map((s) => {
                // On mobile: hide states that aren't "next/relevant" so the bar
                // doesn't get cramped. Only show: current+1 (next action) and Rejected.
                const order = STATES.indexOf(full.review_status);
                const idx = STATES.indexOf(s);
                const isNext = idx === order + 1 || (s === "Collected" && full.review_status === "Ready for Collection");
                const isRejected = s === "Rejected";
                const hideOnMobile = !(isNext || isRejected);
                return (
                  <Button key={s} disabled={busy || full.review_status === s}
                    variant={s === "Rejected" ? "destructive" : s === "Ready for Collection" ? "default" : "outline"}
                    size="default"
                    className={`max-md:h-12 max-md:text-sm max-md:font-semibold ${hideOnMobile ? "max-md:hidden" : ""}`}
                    onClick={() => move(s)} data-testid={`apps-move-${s.replace(/\s+/g, "-").toLowerCase()}`}>
                    {s === "Approved" && <CheckCircle size={14} className="mr-1"/>}
                    {s === "Rejected" && <XCircle size={14} className="mr-1"/>}
                    {s}
                  </Button>
                );
              })}
            </section>
          </div>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className="font-semibold text-sm mt-0.5">{value || "—"}</div>
    </div>
  );
}

function DocumentTile({ k, value }) {
  const labelMap = {
    govt_id_url: "Govt ID",
    institution_id_front_url: "Institution ID – Front",
    institution_id_back_url: "Institution ID – Back",
    senior_citizen_card_url: "Senior Citizen Card",
    disability_card_url: "Disability Certificate",
    freedom_fighter_cert_url: "Freedom Fighter Cert.",
    employee_id_url: "Employee ID",
    address_proof_url: "Address Proof",
    business_registration_url: "Business Registration",
    photo_url: "Passport Photo",
  };
  const label = labelMap[k] || k.replace(/_/g, " ");
  const isPdf = value?.startsWith?.("data:application/pdf");
  const open = () => {
    const w = window.open("");
    if (w) {
      if (isPdf) {
        w.document.write(`<iframe src="${value}" style="width:100%;height:100vh;border:0"></iframe>`);
      } else {
        w.document.write(`<img src="${value}" style="max-width:100%;height:auto"/>`);
      }
    }
  };
  return (
    <button onClick={open} type="button" data-testid={`apps-doc-${k}`}
      className="rounded-lg border bg-card overflow-hidden text-left hover:shadow-md transition-shadow group">
      <div className="aspect-[4/3] bg-muted flex items-center justify-center overflow-hidden">
        {isPdf ? (
          <FileText size={40} className="text-violet-500 group-hover:scale-110 transition-transform"/>
        ) : value ? (
          <img src={value} alt={label} className="w-full h-full object-cover group-hover:scale-105 transition-transform"/>
        ) : (
          <FileText size={32} className="text-muted-foreground opacity-50"/>
        )}
      </div>
      <div className="px-3 py-2">
        <div className="text-[12px] font-bold text-foreground">{label}</div>
        <div className="text-[10px] text-muted-foreground">Click to enlarge</div>
      </div>
    </button>
  );
}


/* ============================================================
   PickupConfirmSection — shown when application is Ready for Collection
   - Shows OTP+token info (already generated by /review)
   - Counter staff types the 6-digit OTP customer reads aloud → marks Collected
============================================================ */
function PickupConfirmSection({ item, full, onConfirmed }) {
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const isCollected = full?.review_status === "Collected";

  const confirm = async () => {
    if (!otp || otp.length !== 6) { toast.error("Enter the 6-digit pickup OTP"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/applications/${item.kind}/${full.id}/confirm-pickup`,
                                       { otp: otp.trim() });
      toast.success(`Pickup confirmed via ${data.method} · marked Collected`);
      onConfirmed && onConfirmed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  if (isCollected) {
    return (
      <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4" data-testid="pickup-collected-banner">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0">
            <CheckCircle size={20} weight="fill"/>
          </div>
          <div className="flex-1">
            <div className="font-bold text-emerald-900">Collected</div>
            <div className="text-[12px] text-emerald-800 mt-0.5">
              Picked up on {(full.pickup_collected_at || "").slice(0, 16).replace("T", " ")}
              {" "}by {full.pickup_collected_by || "—"}
              {full.pickup_collection_method && ` · via ${full.pickup_collection_method}`}.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-violet-200 bg-gradient-to-br from-violet-50 to-indigo-50 rounded-lg p-4 space-y-3" data-testid="pickup-confirm-section">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-violet-600 text-white flex items-center justify-center shrink-0">
          <ShieldCheck size={20} weight="fill"/>
        </div>
        <div className="flex-1">
          <div className="font-bold text-violet-900">Confirm Physical Pickup</div>
          <div className="text-[12px] text-violet-800 mt-0.5">
            Ask the customer for the <b>6-digit Pickup Code</b> shown in their MajheApp
            <i> Track Application</i> screen, OR scan the QR using the
            <a href="/qr-validate" className="underline ml-1 font-semibold">QR Validator</a>.
          </div>
        </div>
      </div>

      {/* OTP demo helper — show counter staff the code (since SMS is mocked) */}
      {full.pickup_otp_demo && (
        <div className="bg-white border border-violet-200 rounded-md px-3 py-2 text-[11.5px] flex flex-wrap items-center gap-x-2 gap-y-1">
          <Hash size={14} className="text-violet-600"/>
          <span className="text-slate-600">Customer&apos;s expected code (demo mode):</span>
          <span className="font-mono font-extrabold text-lg tracking-wider text-violet-900 tabular-nums">
            {full.pickup_otp_demo}
          </span>
          <button type="button"
            data-testid="pickup-otp-copy-helper"
            onClick={() => setOtp(full.pickup_otp_demo)}
            className="ml-auto px-2 py-0.5 rounded bg-violet-600 text-white text-[10px] font-bold uppercase tracking-wide active:scale-95">
            Auto-fill
          </button>
        </div>
      )}

      {/* OTP entry — stacks vertically on mobile so the input is full width */}
      <div className="flex flex-col md:flex-row md:items-stretch gap-2">
        <input value={otp} maxLength={6} inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code"
          data-testid="pickup-otp-input"
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
          placeholder="••••••"
          className="flex-1 px-4 h-14 md:h-12 font-mono text-2xl md:text-lg tracking-[0.5em] md:tracking-[0.4em] text-center bg-white border-2 border-violet-300 rounded-md outline-none focus:border-violet-600 tabular-nums"/>
        <Button onClick={confirm} disabled={busy || otp.length !== 6}
          data-testid="pickup-confirm-btn"
          className="bg-violet-600 hover:bg-violet-700 text-white h-12 md:h-auto px-5 text-base md:text-sm font-semibold">
          {busy ? "Verifying…" : "Confirm Pickup"}
        </Button>
      </div>

      <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
        <Package size={12}/> Once confirmed, the application moves to <b className="text-emerald-700">Collected</b> and the OTP/QR can no longer be reused.
      </div>
    </div>
  );
}
